import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import MainService from '../../services/MainService';

const PAGE_SIZE = 20;
const todayInput = () => new Date().toISOString().split('T')[0];

const parseList = (response) => {
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response)) return response;
  return [];
};
const parseTotal = (response, fallback = 0) => Number(response?.Total ?? response?.total ?? fallback) || 0;
const dateParts = (value) => {
  const date = value ? new Date(value) : new Date();
  return { FromDay: date.getDate(), FromMonth: date.getMonth() + 1, FromYear: date.getFullYear() };
};
const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('en-GB');
};
const formatAmount = (value) => Number(value || 0).toFixed(2);
const statusOptions = ['Processing', 'Payment Done', 'Payment Return'];

const buildClientRequest = (keyword) => ({
  filter: {
    filters: [{ field: 'Name', operator: 'contains', value: keyword }],
    logic: 'and',
  },
});

const ClientFollowupPage = ({ title, endpoint, parentKey, amountLabel, amountKey }) => {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState({ Date: todayInput(), Keyword: '', EmployeeId: '', Status: '', ClientIds: [] });
  const [appliedFilter, setAppliedFilter] = useState(filter);
  const [selectedClients, setSelectedClients] = useState([]);
  const [clientKeyword, setClientKeyword] = useState('');
  const [clientOptions, setClientOptions] = useState([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const isPayment = parentKey === 'ClientPayment';
  const columnCount = isPayment ? 9 : 8;

  const loadEmployees = useCallback(async () => {
    try {
      setEmployees(parseList(await MainService.EmployeesInDropDown()));
    } catch (err) {
      console.error('Load employees error:', err);
    }
  }, []);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService[endpoint]({
        ...dateParts(appliedFilter.Date),
        Keyword: appliedFilter.Keyword?.trim() || '',
        EmployeeId: Number(appliedFilter.EmployeeId) || 0,
        Status: appliedFilter.Status || '',
        ClientIds: appliedFilter.ClientIds || [],
        Skip: (currentPage - 1) * PAGE_SIZE,
        Take: PAGE_SIZE,
      });
      const list = parseList(response);
      setRows(list);
      setTotalCount(parseTotal(response, list.length));
    } catch (err) {
      console.error(`${endpoint} error:`, err);
      setRows([]);
      setTotalCount(0);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  }, [appliedFilter, currentPage, endpoint]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    const keyword = clientKeyword.trim();
    if (keyword.length < 2) {
      setClientOptions([]);
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        setClientLoading(true);
        const response = await MainService.ClientsInAutoComplete(buildClientRequest(keyword));
        setClientOptions(parseList(response).filter((client) => !selectedClients.some((item) => Number(item.Id) === Number(client.Id))));
      } catch (err) {
        console.error('Load clients error:', err);
        setClientOptions([]);
      } finally {
        setClientLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [clientKeyword, selectedClients]);

  const updateFilter = (field, value) => setFilter((prev) => ({ ...prev, [field]: value }));
  const addClient = (client) => {
    const nextClients = [...selectedClients, client];
    setSelectedClients(nextClients);
    setFilter((prev) => ({ ...prev, ClientIds: nextClients.map((item) => item.Id) }));
    setClientKeyword('');
    setClientOptions([]);
  };
  const removeClient = (clientId) => {
    const nextClients = selectedClients.filter((client) => Number(client.Id) !== Number(clientId));
    setSelectedClients(nextClients);
    setFilter((prev) => ({ ...prev, ClientIds: nextClients.map((item) => item.Id) }));
  };
  const filterData = () => {
    setAppliedFilter(filter);
    setCurrentPage(1);
  };
  const refreshData = () => {
    const next = { Date: todayInput(), Keyword: '', EmployeeId: '', Status: '', ClientIds: [] };
    setFilter(next);
    setAppliedFilter(next);
    setSelectedClients([]);
    setClientKeyword('');
    setClientOptions([]);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5 border-b border-gray-200 pb-4">
          <h1 className="text-2xl font-bold text-gray-900">{title} | {formatDate(appliedFilter.Date)}</h1>
          <p className="mt-1 text-sm text-gray-500">Follow up client {title.toLowerCase().replace(' followup', '')} entries by date, keyword, employee, and status.</p>
        </div>
        {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <FilterInput label="Date" type="date" value={filter.Date} onChange={(value) => updateFilter('Date', value)} />
          <FilterSelect label="Employee" value={filter.EmployeeId} onChange={(value) => updateFilter('EmployeeId', value)} options={employees} />
          <FilterInput label="Keyword" value={filter.Keyword} onChange={(value) => updateFilter('Keyword', value)} onEnter={filterData} />
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
            <select value={filter.Status} onChange={(event) => updateFilter('Status', event.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20">
              <option value="">All</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ClientMultiSelect
            keyword={clientKeyword}
            setKeyword={setClientKeyword}
            options={clientOptions}
            selectedClients={selectedClients}
            loading={clientLoading}
            onAdd={addClient}
            onRemove={removeClient}
          />
          <div className="flex items-end gap-3">
            <button type="button" onClick={filterData} disabled={loading} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50">
              <Search className="h-4 w-4" />
              Filter
            </button>
            <button type="button" onClick={refreshData} disabled={loading} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 p-4">
          <h2 className="text-lg font-semibold text-gray-900">{title} List</h2>
          <p className="text-sm text-gray-500">Showing {rows.length} of {totalCount} entries</p>
        </div>
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[1000px] divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>#</Th>
                <Th>Client</Th>
                <Th>Total Orders</Th>
                <Th>Ref No</Th>
                <Th>{isPayment ? 'Payment Ref No' : 'Return Ref No'}</Th>
                {isPayment && <Th>Payment Date</Th>}
                <Th>Employee</Th>
                <Th>Status</Th>
                {isPayment ? <Th>{amountLabel}</Th> : <Th>Client Return Status</Th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <EmptyRow colSpan={columnCount} text="Loading followup entries..." />
              ) : rows.length === 0 ? (
                <EmptyRow colSpan={columnCount} text="No followup entries found." />
              ) : (
                rows.map((row, index) => {
                  const parent = row[parentKey] || {};
                  return (
                    <tr key={row.Id} className="hover:bg-gray-50">
                      <Td>{(currentPage - 1) * PAGE_SIZE + index + 1}</Td>
                      <Td strong>{row.Client?.Name || '-'}</Td>
                      <Td>{row.TotalOrders ?? parent.TotalOrders ?? 0}</Td>
                      <Td>{row.RefNo || '-'}</Td>
                      <Td>{parent.RefNo || '-'}</Td>
                      {isPayment && <Td>{formatDate(parent.Date)}</Td>}
                      <Td>{parent.EmployeeName || '-'}</Td>
                      <Td>{row.Status || '-'}</Td>
                      {isPayment ? (
                        <Td>{formatAmount(parent[amountKey] ?? row[amountKey])}</Td>
                      ) : (
                        <Td>{parent.Status || '-'}</Td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between">
          <span>Showing {rows.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0} to {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount}</span>
          <div className="flex gap-2">
            <button type="button" onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={currentPage <= 1 || loading} className="rounded-lg border border-gray-300 px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">Previous</button>
            <span className="px-3 py-1.5">Page {currentPage} of {totalPages}</span>
            <button type="button" onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))} disabled={currentPage >= totalPages || loading} className="rounded-lg border border-gray-300 px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const FilterInput = ({ label, value, onChange, type = 'text', onEnter }) => (
  <div>
    <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
    <input type={type} value={value} onChange={(event) => onChange(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && onEnter) onEnter(); }} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
  </div>
);

const FilterSelect = ({ label, value, onChange, options }) => (
  <div>
    <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
    <select value={value} onChange={(event) => onChange(event.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20">
      <option value="">All</option>
      {options.map((option) => <option key={option.Id} value={option.Id}>{option.Name}</option>)}
    </select>
  </div>
);

const ClientMultiSelect = ({ keyword, setKeyword, options, selectedClients, loading, onAdd, onRemove }) => (
  <div className="relative">
    <label className="mb-1 block text-sm font-medium text-gray-700">Clients</label>
    <input
      type="text"
      value={keyword}
      onChange={(event) => setKeyword(event.target.value)}
      placeholder="Search and select clients..."
      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
    />
    {selectedClients.length > 0 && (
      <div className="mt-2 flex flex-wrap gap-2">
        {selectedClients.map((client) => (
          <span key={client.Id} className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
            {client.Name}
            <button type="button" onClick={() => onRemove(client.Id)} className="text-primary hover:text-red-600">x</button>
          </span>
        ))}
      </div>
    )}
    {keyword.trim().length >= 2 && (
      <div className="absolute z-40 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
        {loading ? (
          <div className="px-3 py-2 text-sm text-gray-500">Searching clients...</div>
        ) : options.length === 0 ? (
          <div className="px-3 py-2 text-sm text-gray-500">No clients found</div>
        ) : (
          options.map((client) => (
            <button
              key={client.Id}
              type="button"
              onClick={() => onAdd(client)}
              className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
            >
              <span className="font-medium">{client.Name}</span>
              {client.MobileNo1 && <span className="ml-2 text-xs text-gray-500">{client.MobileNo1}</span>}
            </button>
          ))
        )}
      </div>
    )}
  </div>
);

const Th = ({ children }) => <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">{children}</th>;
const Td = ({ children, strong = false }) => <td className={`whitespace-nowrap px-4 py-3 text-sm ${strong ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{children}</td>;
const EmptyRow = ({ colSpan, text }) => <tr><td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-gray-500">{text}</td></tr>;

export default ClientFollowupPage;
